import { Component, EventEmitter, OnDestroy, OnInit, Output, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { first } from 'rxjs/operators';

import { ModalService } from '../../../core/services/modal.service';
import { OrderService } from '../../../core/services/data/order.service';
import { UserService } from '../../../core/services/data/user.service';
import { BooksService } from '../../../core/services/data/books.service';
import { AnalyticsService } from '../../../core/services/analytics.service';
import { VARIANT_CHECKOUT_ITEM } from '../../../shared/cart-item/cart-item.component';
import { SettingsService } from '../../../core/services/data/settings.service';
import { PurchaseProcessService } from '../../../core/services/data/purchase-process.service';
import { LogsService } from '../../../core/services/logs.service';
import { GenreService } from '../../../core/services/data/genre.service';
import { IPopulatedGenres } from '../../../types/populated';
import { StripeService } from '../../../core/services/stripe.service';
import { PromoStackPurchaseService } from '../../../core/services/data/promo-stack-purchase.service';
import { LocalStorageService } from '../../../core/services/data/local-storage.service';
import { GiveawayService } from '../../../core/services/data/giveaway.service';

const PAYMENT_TYPE_PAYPAL = 'paypal';
const PAYMENT_TYPE_STRIPE = 'stripe';
const PAYMENT_TYPE_COUPON = 'coupon';
const AUTHOR_DASHBOARD = '/author-dashboard';
const FEATURES_PATH = '/features';
/*
 * Stripe has a limit on the number of meta data keys you can add
 * https://stripe.com/docs/api/metadata
 */
const STRIPE_META_DATA_KEY_LIMIT = 50;

const PROMO_TYPE_PERCENT = 'percent';
const PROMO_TYPE_AMOUNT = 'amount';

@Component({
	selector: 'app-features-cart-checkout',
	templateUrl: './features-cart-checkout.component.html',
	styleUrls: ['../features-step5/features-step5.component.scss', './features-cart-checkout.component.scss'],
})
export class FeaturesCartCheckoutComponent implements OnInit, OnDestroy {
	@Output() soldOutEvent = new EventEmitter();

	@Input() cartPurchaseEnabled;

	public order;

	public orderTotalAmount = 0;

	public order$;

	public paymentInProgress = false;

	public purchaseInProgressMessages = [
		'Processing Payment Please Wait',
		'Reserving Your Features Please Wait',
		'Finalizing Your Order Please Wait',
		'Sending Your Receipt Please Wait',
	];

	public learnMoreAboutMembershipLink;

	public updateInProgress = false;

	public loadingOrder = false;

	public purchaseInProgressMessage = 'Processing Payment Please Wait';

	public errorTitle;

	public errorText;

	public errorExtra;

	public soldOut = false;

	public cartItemVariant = VARIANT_CHECKOUT_ITEM;

	public stripeCheckoutEnabled = false;

	public genres: IPopulatedGenres[];

	public qualifiesForMembershipUpsell = false;

	public checkoutSignupLogin = false;

	public author;

	public errorOptions = {
		title: 'There was a problem',
		text: null,
		actions: [{
			type: 'solid',
			text: 'Got it',
			callback: () => this.ms.close('cart-error-modal'),
		}],
	};

	private refresh$;

	private paypalId;

	private author$;

	constructor(
		private ms: ModalService,
		private os: OrderService,
		private router: Router,
		private route: ActivatedRoute,
		private bs: BooksService,
		private us: UserService,
		private as: AnalyticsService,
		private ss: SettingsService,
		private purchaseProcessService: PurchaseProcessService,
		private ls: LogsService,
		private genreService: GenreService,
		private stripeService: StripeService,
		private promoStackPurchaseService: PromoStackPurchaseService,
		private localStorageService: LocalStorageService,
		private giveawayService: GiveawayService,
	) { }

	ngOnInit() {
		this.os.cartUpdating$.subscribe((data: boolean) => {
			this.updateInProgress = data;
		});
		this.loadingOrder = true;
		this.order$ = this.os.orderStore$.subscribe(async (data: any) => {
			this.loadingOrder = false;
			this.order = data;
			this.os.initiateCheckout(this.order._id).pipe(first()).subscribe(() => {});
			this.orderTotalAmount = this.os.calculateOrderTotal();

			const setting = await this.ss.getMembershipUpsellSetting();

			this.qualifiesForMembershipUpsell = this._qualifyForMembershipUpsell(setting);
			this.handleBeginCheckoutAnalytics(data);
		});
		this.author$ = this.us.author$.subscribe(
			(data: any) => {
				this.author = data;
				this.learnMoreAboutMembershipLink = `/author-dashboard/${this.author.authorLink}/subscriptions`;
			},
		);

		this.ss.stripeCheckoutEnabled().then(
			(value) => {
				// this.stripeCheckoutEnabled = !!value;
				this.stripeCheckoutEnabled = !!value;
			},
		);

		this.ss.getCheckoutSignupLoginSetting().then(
			(value) => {
				this.checkoutSignupLogin = !!value;
			},
		);

		this.genreService.getGenres().subscribe((genres: IPopulatedGenres[]) => {
			this.genres = genres;
		});

		this.refresh$ = this.os.orderRefresh.subscribe((orderId) => {
			if (orderId === this.order._id) {
				this.os.refreshCart();
			}
		});
	}

	async ngOnDestroy() {
		this.order$?.unsubscribe();
		this.author$?.unsubscribe();
		this.refresh$?.unsubscribe();
	}

	progressStatusMessages() {
		[this.purchaseInProgressMessage] = this.purchaseInProgressMessages;
		let i = 0;
		const interval = setInterval(() => {
			i += 1;
			if (i === this.purchaseInProgressMessages.length) {
				clearInterval(interval);
				return;
			}
			this.purchaseInProgressMessage = this.purchaseInProgressMessages[i];
		}, 2000);
	}

	reducePrice(item) {
		const { item: _item } = item;
		return _item.paymentAmount || 0;
	}

	addSubscriptionToCart() {
		const goldPlan = '62f27ba988466185204dc935';
		const { purchaserInfo } = this.order;

		const userInfo = {
			username: purchaserInfo.email,
			firstName: purchaserInfo.firstName,
			lastName: purchaserInfo.lastName,
		};

		this.os.addSubscriptionToCart(goldPlan, userInfo).subscribe(
			(data: any) => {
				this.os.refreshCart();
				this.closeMembershipUpsell();
			},
		);
	}

	handleBeginCheckoutAnalytics(data) {
		if (data && data.items && data.items.length) {
			const { items } = data;
			const analyticsCartItems = [];

			// Loop through all the cart items to calculate the total order amount
			items.forEach(({ item }) => {
				if (item._site && item._siteAdvert) {
					const { _site, _siteAdvert, paymentAmount } = item;
					const { seoSlug, name, _parentGenre } = _siteAdvert;

					let parentGenre = 'N/A';

					if (_parentGenre) {
						parentGenre = this.genreService.getGenreNameById(this.genres, _parentGenre);
					}

					const ecommerce = this.as.generateAnalyticsEcommerceObject(
						seoSlug,
						name,
						_site.name,
						parentGenre,
						paymentAmount,
						1,
						item.purchaseSource || _site.name,
						item.purchaseSource,
					);
					analyticsCartItems.push(ecommerce);
				}
			});

			this.as.trackBeginCheckout(this.orderTotalAmount, analyticsCartItems);
		}
	}

	openMembershipUpsell() {
		this.ms.open('membership-upsell-modal');
	}

	closeMembershipUpsell() {
		this.ms.close('membership-upsell-modal');
	}

	openMembershipRemovalConfirm() {
		this.ms.open('membership-removal-confirmation');
	}

	closeMembershipRemovalConfirm() {
		this.ms.close('membership-removal-confirmation');
	}

	handleSoldOut(featureId) {
		const { items } = this.order;
		const orderItem = items.find(({ itemId }) => itemId === featureId);

		if (orderItem.itemType === 'featuredBook') {
			const feature = orderItem.item;

			const {
				ASIN,
				whereAvailable,
				title,
				description,
				bookLength,
				nameOfSeries,
				bookNumber,
				numberOfBooksInSeries,
				threesomesOrRomanticScenes,
				endingOfBook,
				cheatingOrInfidelity,
				commonThemes,
				_site,
				_siteAdvert,
				_book: { link },
			} = feature;

			this.purchaseProcessService.setDetails({
				feature: {
					ASIN,
					whereAvailable,
				},
				book: {
					ASIN,
					amazonLink: `https://www.amazon.com/dp/${ASIN}`,
					whereAvailable,
					title,
					description,
					bookLength,
					nameOfSeries,
					bookNumber,
					numberOfBooksInSeries,
					threesomesOrRomanticScenes,
					endingOfBook,
					cheatingOrInfidelity,
					commonThemes,
					link,
				},
			}, 'soldout');

			let _link = `features/${_site.seoSlug}/${_siteAdvert.seoSlug}`;
			if (this.author && this.author.authorLink) {
				_link = `${AUTHOR_DASHBOARD}/${this.author.authorLink}/features/${_site.seoSlug}/${_siteAdvert.seoSlug}`;
			}
			this.os.setEditingItemId(feature._id);
			this.os.setCartNotVisible();
			this.router.navigate(['/']).then(() => this.router.navigate([_link], { queryParams: { feature: featureId } }).then());
		}

		if (orderItem.itemType === 'promoStack') {
			this.promoStackPurchaseService.editPromoStack(orderItem.item);
			const link = this.promoStackPurchaseService.getEditDateLink();
			const authorPath = this.author ? `${AUTHOR_DASHBOARD}/${this.author.authorLink}` : '';
			this.router.navigate([authorPath + link]);
		}
	}

	freePurchase() {
		this.paymentInProgress = true;
		const thirtyDayError = this.os.checkOrderForThirtyDayViolation(this.order);
		if (thirtyDayError && thirtyDayError.errorText) {
			// this.errorText = [thirtyDayError.errorText];
			this.paymentInProgress = false;

			this.errorOptions.text = [thirtyDayError.errorText];
			this.ms.open('cart-error-modal');
		} else {
			this.os.checkOrderSiteInventory(this.order._id).subscribe((data: any) => {
				if (data.success && data.invalidFeatures && data.advertsWithInventoryConflict) {
					if (this.us.isAdminOrSuperAdmin() || (!data.invalidFeatures.length && !data.advertsWithInventoryConflict.length)) {
						const completedOrderObj = {
							orderId: this.order._id,
							paymentAmount: this.orderTotalAmount,
							url: window.location.href,
							messageId: this.localStorageService.getData('messageId'),
						};
						this.os.completeFreeCartOrder(completedOrderObj).subscribe(
							(result: any) => {
								this.paymentInProgress = false;
								if (result.success) {
									this.router.navigate(['success'], { relativeTo: this.route }).then();
									this.os.clearStoreOrder();
								} else {
									// this.errorText = [result.message];
									this.errorOptions.text = [result.message];
									this.ms.open('cart-error-modal');
								}
							},
						);
					} else if (data.advertsWithInventoryConflict.length) {
						// There is an oversell conflict for a site advert
						const { text, title } = this.os.parseAdvertInventoryConflictError(data.advertsWithInventoryConflict, this.order);
						// this.errorText = [text];
						// this.errorTitle = title;
						this.paymentInProgress = false;
						this.errorOptions.title = title;
						this.errorOptions.text = [text];

						this.ms.open('cart-error-modal');
					} else {
						// There is an invalid feature
						const invalidFeatures = this.order.items.filter(({ itemId }) => data.invalidFeatures.includes(itemId));
						const invalidFeatureMeta = {};
						invalidFeatures.forEach((feature) => {
							const { item: { _book, pubDate, _id } } = feature;
							invalidFeatureMeta[_id] = `${_book.title} by ${_book.author} on `
                + `${moment(pubDate, 'YYYYMMDD').format('MMMM Do, YYYY')}.`;
						});
						this.paymentInProgress = false;
						this.soldOut = true;
						// 			this.errorText = ['The dates you selected for the following features are no longer available.'
						// + ' Please select a new one.'];

						this.errorOptions.title = 'Dang. That date is no longer available';
						this.errorOptions.text = ['The dates you selected for the following features are no longer available.'
							+ ' Please select a new one.'];
						this.errorExtra = invalidFeatureMeta;

						this.errorOptions.actions = [
							{
								type: 'solid',
								text: 'Pick Another Date',
								callback: () => {
									const invalidFeatureMetaIds = Object.keys(invalidFeatureMeta);

									this.ms.close('cart-error-modal');
									this.handleSoldOut(invalidFeatureMetaIds[0]);
								},
							},
						];

						this.ms.open('cart-error-modal');
					}
				} else {
					const errorMsg = data.message || 'Something went wrong.';
					this.paymentInProgress = false;
					// this.errorText = [errorMsg];

					this.errorOptions.text = [errorMsg];
					this.ms.open('cart-error-modal');
				}
			});
		}
	}

	handlePayPalPayment(event) {
		this.paymentInProgress = true;
		this.progressStatusMessages();
		const transactionId = event.data.purchase_units?.[0]?.payments?.captures?.[0]?.id;
		const paymentAmount = event.data.purchase_units?.[0]?.amount?.value;

		const completeOrderObj: any = {
			orderId: this.order._id,
			paymentAmount,
			url: window.location.href,
			payPalTransactionID: transactionId,
		};
		completeOrderObj.payPalToken = {
			cancelled: false,
			paid: true,
			payerID: event.data.payer?.payer_id,
			payerEmail: event.data.payer?.email_address,
			paymentID: event.data?.id,
		};
		this.paypalId = completeOrderObj.payPalToken;
		this.os.completeCartOrder(completeOrderObj).subscribe(
			(data: any) => {
				this.paymentInProgress = false;
				if (data.success) {
					this.router.navigate(['success'], { relativeTo: this.route }).then();
					this.os.clearStoreOrder();
				} else {
					// this.errorText = [data.message];
					this.errorOptions.text = [data.message];
					this.ms.open('cart-error-modal');
				}
			},
		);
		this.triggerPurchaseAnalytics();
	}

	handleStripePayment(event) {
		const itemCount = this.order.items.length;
		this.paymentInProgress = true;
		this.os.checkOrderSiteInventory(this.order._id).subscribe((data: any) => {
			if (data.success && data.invalidFeatures && data.advertsWithInventoryConflict) {
				if (!data.invalidFeatures.length && !data.advertsWithInventoryConflict.length) {
					const completedOrderObj = {
						orderId: this.order._id,
						paymentAmount: this.orderTotalAmount,
						url: window.location.href,
						description: `Purchase of ${itemCount} ${itemCount > 1 ? 'items' : 'item'}. OrderId: ${this.order._id}`,
						source: { ...event.data, object: 'card' },
						metadata: {
							order_id: this.order._id,
						},
						stripeCustomerId: this.author?.stripeCustomerId,
						messageId: this.localStorageService.getData('messageId'),
					};
					delete completedOrderObj.source.rememberMe;
					this.order.items.forEach(({ item }, index) => {
						const itemPrefix = index + 1;
						/*
						* Safe guard to prevent more than STRIPE_META_DATA_KEY_LIMIT from being added
						* to the Stripe meta data object. Doing so wil cause the transaction to fail and throw an error
						*/
						if (itemPrefix < STRIPE_META_DATA_KEY_LIMIT) {
							const itemMetadata = {
								[`item_${itemPrefix}_ID`]: item._id,
							};
							completedOrderObj.metadata = {
								...completedOrderObj.metadata,
								...itemMetadata,
							};
						}
					});

					const _order = { ...this.order };

					this.ls.postLog(completedOrderObj);
					this.os.createStripePayment(completedOrderObj).subscribe(
						(result: any) => {
							this.paymentInProgress = false;
							if (result.success) {
								this.triggerEmailSchedule(_order.items);
								this.router.navigate(['success'], { relativeTo: this.route }).then();
								this.os.clearStoreOrder();
							} else {
								// this.errorText = [result.message];
								this.errorOptions.text = [result.message];
								this.ms.open('cart-error-modal');
							}
						},
					);
					this.triggerPurchaseAnalytics();
				} else if (data.advertsWithInventoryConflict.length) {
					// There is an oversell conflict for a site advert
					const { text, title } = this.os.parseAdvertInventoryConflictError(data.advertsWithInventoryConflict, this.order);
					// this.errorText = [text];
					// this.errorTitle = title;
					this.paymentInProgress = false;

					this.errorOptions.title = title;
					this.errorOptions.text = [text];
					this.ms.open('cart-error-modal');
				} else {
					// There is an invalid feature
					const invalidFeatures = this.order.items.filter(({ itemId }) => data.invalidFeatures.includes(itemId));
					const invalidFeatureMeta = {};
					invalidFeatures.forEach((feature) => {
						const { item: { _book, pubDate, _id } } = feature;
						invalidFeatureMeta[_id] = `${_book.title} by ${_book.author} on `
              + `${moment(pubDate, 'YYYYMMDD').format('MMMM Do, YYYY')}.`;
					});
					this.paymentInProgress = false;
					this.soldOut = true;
					// 			this.errorText = ['The dates you selected for the following features are no longer available.'
					//   + ' Please select a new one.'];
					this.errorExtra = invalidFeatureMeta;

					this.errorOptions.title = 'Dang. That date is no longer available';
					this.errorOptions.text = ['The dates you selected for the following features are no longer available.'
						+ ' Please select a new one.'];

					this.errorOptions.actions = [
						{
							type: 'solid',
							text: 'Pick Another Date',
							callback: () => {
								const invalidFeatureMetaIds = Object.keys(invalidFeatureMeta);

								this.ms.close('cart-error-modal');
								this.handleSoldOut(invalidFeatureMetaIds[0]);
							},
						},
					];

					this.ms.open('cart-error-modal');
				}
			} else {
				this.paymentInProgress = false;
				const errorMsg = data.message || 'Something went wrong.';
				// this.errorText = [errorMsg];
				this.errorOptions.text = [errorMsg];
				this.ms.open('cart-error-modal');
			}
		});
	}

	triggerEmailSchedule(orderItems) {
		const giveaways = orderItems.filter(({ item: { _siteAdvert } }) => _siteAdvert?.giveaway?.isGiveaway);

		if (giveaways.length) {
			giveaways.forEach(({ item: { _id, _siteAdvert } }) => {
				this.giveawayService.scheduleEmail(_id).subscribe();
			});
		}
	}

	async handlePaymentSuccess(event) {
		// eslint-disable-next-line no-restricted-syntax
		try {
			if (event.type === PAYMENT_TYPE_PAYPAL) {
				await this._cancelStripePendingPaymentIntents();
				this.handlePayPalPayment(event);
			}
			if (event.type === PAYMENT_TYPE_STRIPE) {
				this.handleStripePayment(event);
			}
		} catch (err) {
			this.ls.postLog({
				type: 'PayPal',
				message: err.message,
			});
		}
	}

	handlePaymentStatus(event) {
		// eslint-disable-next-line no-prototype-builtins
		this.paymentInProgress = event && event.hasOwnProperty('loading') ? event.loading : false;
	}

	startOver() {
		this.purchaseProcessService.clearDetails();
		this.router.navigate([FEATURES_PATH]).then();
	}

	triggerPurchaseAnalytics() {
		const checkoutStep = 3;
		// ecommerce object for GTM
		const promoCode = [];
		const cartItems = this.order.items.map(({ item }) => {
			let { paymentAmount } = item;
			if (item._promoCode && typeof item._promoCode === 'object') {
				promoCode.push(`${item._promoCode.code}`);
				if (item._promoCode.discountType === PROMO_TYPE_PERCENT) {
					const promoAmount = Math.round(((paymentAmount * item._promoCode.value) / 100) * 100) / 100;
					paymentAmount -= promoAmount;
				} else if (item._promoCode.discountType === PROMO_TYPE_AMOUNT) {
					const promoAmount = item._promoCode.value;
					paymentAmount -= promoAmount;
				}
			}

			if (item._siteAdvert && item._siteAdvert._site) {
				return {
					id: item._siteAdvert.seoSlug,
					name: `${item._site.name} ${item._siteAdvert.name} Feature`,
					category: `${item._site.name}/${item._siteAdvert.name}`,
					price: paymentAmount,
					quantity: 1,
					dimension1: item._book.author,
					dimension2: this.author ? this.author._id : '',
				};
			}

			return null;
		}).filter((item) => !!item);

		if (!cartItems.length) {
			return;
		}

		const ecommerce = {
			currencyCode: 'USD',
			purchase: {
				actionField: {
					id: this.order._id,
					coupon: promoCode.length ? `${promoCode.join(',')}` : null,
					revenue: this.orderTotalAmount,
				},
				products: cartItems,
			},
		};
		let paymentType = PAYMENT_TYPE_PAYPAL;
		if (!this.paypalId) {
			paymentType = PAYMENT_TYPE_STRIPE;
		}
		if (this.orderTotalAmount === 0) {
			paymentType = PAYMENT_TYPE_COUPON;
		}
		this.as.trackCheckoutOption(checkoutStep, paymentType);
		this.as.trackGTMEvent('eec.purchase', { ecommerce });
	}

	_qualifyForMembershipUpsell(shouldDisplayUpsell) {
		if (!shouldDisplayUpsell) {
			return false;
		}

		// If the user is not a member, and the cart contains a subscription
		// and the cart does not contain a membership upsell
		if (this.author && this.author?.memberRole?.roleName !== 'Free') {
			return false;
		}

		const existingSubscriptionInCart = this.os.getSubscriptionFromOrder();

		if (existingSubscriptionInCart) {
			return false;
		}

		if (!this.author && !this.checkoutSignupLogin) {
			return false;
		}

		return true;
	}

	_cancelStripePendingPaymentIntents() {
		if (!this.author) return;
		const body = {
			customer: this.author?.stripeCustomerId,
			orderId: this.order._id,
		};

		this.stripeService.cancelPendingPaymentIntents(body).subscribe();
	}
}
